import type { XPEntry } from '@/domain/xp'
import type { XPRepository } from '@/repositories/XPRepository'

const STORAGE_KEY = 'cybertales.xp'

function readAll(): XPEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as XPEntry[]) : []
  } catch {
    return []
  }
}

function writeAll(items: XPEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // best-effort — XP simply won't persist across reloads.
  }
}

// Zero-cost implementation of XPRepository (Document 11 §4). Enforces the
// idempotent-award contract from the interface: an entry whose
// sourceEventId has already been recorded for that user is silently
// skipped, so a re-fired domain event can never double-award XP.
export class LocalXPRepository implements XPRepository {
  async award(entry: XPEntry): Promise<void> {
    const all = readAll()
    const alreadyAwarded = all.some(
      (e) => e.userId === entry.userId && e.sourceEventId === entry.sourceEventId
    )
    if (alreadyAwarded) return
    all.push(entry)
    writeAll(all)
  }

  async listForUser(userId: string): Promise<XPEntry[]> {
    return readAll().filter((e) => e.userId === userId)
  }

  async getTotalForUser(userId: string): Promise<number> {
    return (await this.listForUser(userId)).reduce((sum, e) => sum + e.amount, 0)
  }
}
